/**
 * resolveKoDiscardChoice move + the KO-from-discard eligibility predicate and
 * block-all guard (WP-693 / D-24510).
 *
 * Called by the current player after Loki's "Maniacal Tyrant" tactic Fight ("KO up
 * to four cards from your discard pile") parked a PendingKoDiscardChoice. The player
 * selects 0..min(4, discardSize) cards from their OWN discard; this move
 * validates the selection against the current discard, removes each chosen card from
 * discard, appends it to the global KO pile (G.ko) via koCard, and front-pops the
 * pending entry. An empty selection is the legal "KO nothing" choice.
 *
 * This is the first BOUNDED, OPTIONAL 0..N-cap multi-select resolve in the engine:
 * the exact-count siblings (resolvePutCardsOnDeckChoice, resolveReorderChoice)
 * reject any count other than their fixed `count`. It mirrors their conventions —
 * array payload, FIFO queue, front-only resolve, front-pop on success, block-all
 * guard, silent no-ops on every invalid payload with the queue left intact.
 *
 * // why: D-24682 (supersedes D-24510's distinct-id rule) — the payload is a MULTISET.
 * Basic S.H.I.E.L.D. Agents / Troopers / Officers and Wounds share one ext_id per kind
 * (fungible tokens, D-24183), so "KO two S.H.I.E.L.D. Agents" is the same id twice. Each
 * repeated id removes one more copy; asking for more copies than the discard holds is
 * rejected by the per-card presence check on the working copy.
 *
 * No registry imports. No .reduce(). Moves never throw.
 */
import { moveCardFromZone } from './zoneOps.js';
import { koCard } from '../board/ko.logic.js';
import { recordOwnedCardKoed } from '../hero/deferredConditionalGrants.js';
import { pushLog } from '../log/logPush.js';
import { formatCardRef } from '../log/logDisplay.js';
import { matchesGreyHeroFilter } from '../hero/splitCard.logic.js';
/**
 * Whether any KO-from-discard choice is currently pending.
 *
 * Single predicate imported by the block-all action-move guards. `undefined` and
 * `[]` both mean no pending choice (mirrors hasPendingDefeatChoice, D-24291).
 *
 * @param G - The game state to inspect (not mutated).
 * @returns true when the pending-KO-from-discard queue holds at least one entry.
 */
export function hasPendingKoDiscardChoice(G) {
    return (G.pendingKoDiscardChoices?.length ?? 0) > 0;
}
/**
 * Returns the cards eligible to be KO'd from a player's discard — the whole discard
 * pile, in current order. Recomputed fresh (no snapshot): the block-all guard freezes
 * the discard while the choice is pending, so the projection and the resolve move both
 * read the live discard and agree byte-for-byte (the round-trip rule).
 *
 * With a grey-Hero filter (WP-836 / D-24769) only the discard cards passing it are
 * returned, still in discard order with duplicates kept.
 *
 * With a card-instance filter (WP-850 / D-24786) a discard card is kept only while the
 * filter still allows another copy of its id, so the result is the multiset intersection
 * of the filter and the live discard, in discard order. The grey filter then applies on top.
 *
 * @param G - The game state to inspect (not mutated).
 * @param playerID - The chooser whose discard is the KO source.
 * @param greyFilter - An optional grey-Hero filter on the KO.
 * @param eligibleCardIds - An optional card-instance filter (a multiset of ids).
 * @returns The chooser's eligible discard cards in order, or an empty array when the player is unknown.
 */
export function getEligibleKoDiscardCards(G, playerID, greyFilter, eligibleCardIds) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return [];
    }
    if (greyFilter === undefined && eligibleCardIds === undefined) {
        return [...playerZones.discard];
    }
    // why: WP-850 / D-24786 — the filter is a multiset: Wounds and basic S.H.I.E.L.D. cards share
    // one ext_id per kind (D-24682), so "those cards" may hold an id twice, and the live discard
    // may hold more copies than this play discarded. Each kept card spends one allowance.
    const remainingAllowance = eligibleCardIds === undefined ? undefined : countCardIds(eligibleCardIds);
    const eligibleCards = [];
    for (const cardId of playerZones.discard) {
        if (remainingAllowance !== undefined) {
            const allowance = remainingAllowance.get(cardId) ?? 0;
            if (allowance === 0) {
                continue;
            }
            remainingAllowance.set(cardId, allowance - 1);
        }
        if (greyFilter !== undefined && !matchesGreyHeroFilter(G, cardId, greyFilter)) {
            continue;
        }
        eligibleCards.push(cardId);
    }
    return eligibleCards;
}
/**
 * Counts how many times each card id appears in a list.
 *
 * @param cardIds - The ids to count (not mutated).
 * @returns A map from each id to its number of occurrences.
 */
function countCardIds(cardIds) {
    const counts = new Map();
    for (const cardId of cardIds) {
        counts.set(cardId, (counts.get(cardId) ?? 0) + 1);
    }
    return counts;
}
/**
 * Whether every id in a selection is allowed by a multiset filter — each id may appear at
 * most as many times as it appears in the filter (WP-850 / D-24786).
 *
 * @param selection - The ids the player chose (not mutated).
 * @param allowedCardIds - The multiset filter (not mutated).
 * @returns true when the selection is a sub-multiset of the filter.
 */
function isSubMultiset(selection, allowedCardIds) {
    const remainingAllowance = countCardIds(allowedCardIds);
    for (const cardId of selection) {
        const allowance = remainingAllowance.get(cardId) ?? 0;
        if (allowance === 0) {
            return false;
        }
        remainingAllowance.set(cardId, allowance - 1);
    }
    return true;
}
/**
 * Resolves the FRONT pending KO-from-discard choice by knocking out the chosen
 * cards from the player's own discard into the global KO pile.
 *
 * Validate args → validate the front pending entry → the selection must be 0..maxCount
 * ids all present in the chooser's discard (a repeated id needs that many copies) →
 * remove each from discard, append to G.ko → front-pop on success. Silent no-ops (queue
 * intact — resubmit): non-array / non-string ids; more ids than maxCount; empty queue;
 * front.playerID mismatch; front.choiceType mismatch; any id absent from the discard, or
 * repeated more times than the discard holds copies. An empty selection is the legal
 * "KO nothing" choice and still front-pops.
 *
 * @param context - boardgame.io move context with G and playerID.
 * @param args - the selected { cardIds } to KO from the discard pile.
 */
export function resolveKoDiscardChoice({ G, playerID }, args) {
    // Step 1: Validate args — cardIds must be an array of non-empty strings. An EMPTY
    // array is legal here ("KO up to four" includes zero), unlike the exact-count siblings.
    // why: a client may submit the move with no payload (undefined) or null; reading a field off
    // either throws a TypeError, and moves never throw — reject before any field read.
    if (args === null || typeof args !== 'object') {
        return;
    }
    if (!Array.isArray(args.cardIds)) {
        return;
    }
    for (const cardId of args.cardIds) {
        if (typeof cardId !== 'string' || cardId.length === 0) {
            return;
        }
    }
    // Step 2: Validate the front pending entry — front-only resolution (no index in the
    // payload, so a non-front entry can never be targeted).
    const queue = G.pendingKoDiscardChoices;
    if (queue === undefined || queue.length === 0) {
        return;
    }
    const front = queue[0];
    if (front.playerID !== playerID) {
        return;
    }
    if (front.choiceType !== 'ko-from-discard') {
        return;
    }
    // why: D-24510 — reject over-cap selections. The cap is the printed "up to four"
    // (front.maxCount); a selection larger than it is invalid (the discard-size clamp is
    // enforced implicitly by the in-discard presence check below).
    if (args.cardIds.length > front.maxCount) {
        return;
    }
    // why: WP-836 / D-24769 — a filtered entry (Destroyer / Frost Giant Warrior / Ymir: grey
    // Heroes only) rejects the whole selection when any id fails the filter, queue intact.
    if (front.greyFilter !== undefined) {
        for (const cardId of args.cardIds) {
            if (!matchesGreyHeroFilter(G, cardId, front.greyFilter)) {
                return;
            }
        }
    }
    // why: WP-850 / D-24786 — a card-instance filter (Firepower from the Future: "KO any number
    // of those cards") rejects the whole selection when it names a card outside the filter, or
    // more copies of an id than the filter allows, queue intact.
    if (front.eligibleCardIds !== undefined && !isSubMultiset(args.cardIds, front.eligibleCardIds)) {
        return;
    }
    // Step 3: Resolve against CURRENT G — each chosen id must be present in the chooser's
    // discard now. The block-all guard freezes the discard while pending, so the discard
    // the client saw is the discard we validate against. Remove each id from a WORKING copy
    // first; if any id is absent the whole selection is invalid and we no-op with the real
    // discard untouched (queue intact — resubmit).
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    let workingDiscard = playerZones.discard;
    const chosenCards = [];
    for (const cardId of args.cardIds) {
        const moveResult = moveCardFromZone(workingDiscard, [], cardId);
        if (!moveResult.found) {
            return;
        }
        workingDiscard = moveResult.from;
        chosenCards.push(cardId);
    }
    // Step 4: Mutate — commit the validated discard and append each chosen card to the
    // global KO pile via koCard (destination-only: the card was already removed from
    // discard above, satisfying koCard's remove-before-append contract).
    playerZones.discard = workingDiscard;
    for (const cardId of chosenCards) {
        G.ko = koCard(G.ko, cardId);
        recordOwnedCardKoed(G, playerID, cardId, 'discard');
    }
    // Step 5: Narrate the resolved KO. `G.messages` is hash-excluded (D-24081), so this
    // adds no determinism / sentinel impact.
    // why: WP-760 / D-24589 — two cards now park this choice. Salomé's entry carries
    // `sourceCardId`, named from G.cardDisplayData; Loki's Maniacal Tyrant entry never sets
    // it, so its log line stays byte-identical.
    let sourceName = 'Maniacal Tyrant';
    if (front.sourceCardId !== undefined) {
        sourceName = resolveSourceCardName(G, front.sourceCardId);
    }
    // why: D-24783 — name each KO'd card like the other KO lines (`{Name} ({ext-id})`), so a
    // grey KO (Frost Giant Warrior / Ymir) says WHICH starters went; zero reads "no cards".
    const koCardRefs = [];
    for (const cardId of chosenCards) {
        koCardRefs.push(formatCardRef(G.cardDisplayData, cardId));
    }
    const koSummary = koCardRefs.length === 0 ? 'no cards' : koCardRefs.join(', ');
    pushLog(G, `Player ${playerID} KO'd ${koSummary} from their discard pile (${sourceName}).`, 'applied');
    // Step 6: Front-pop ONLY on success (front-pop = Array.shift). An empty selection is a
    // successful "KO nothing" and still pops so the board unfreezes.
    queue.shift();
}
/**
 * Resolves the display name of the card that parked a KO-from-discard choice.
 *
 * @param G - Current game state (read-only).
 * @param sourceCardId - The parking card's ext_id.
 * @returns The card's display name, or the raw ext_id when no name is available.
 */
function resolveSourceCardName(G, sourceCardId) {
    const display = G.cardDisplayData?.[sourceCardId];
    if (display && typeof display.name === 'string' && display.name.length > 0) {
        return display.name;
    }
    return sourceCardId;
}
//# sourceMappingURL=koDiscardChoice.resolve.js.map